// Theme variants
export const THEME_VARIANTS = {
  LIGHT: 'light',
  DARK: 'dark',
  PINK: 'pink'
};

// Default colors
export const DEFAULT_COLORS = {
  PRIMARY: '#FF4D4F',
  SECONDARY: '#CE93D8'
};

// Font families
export const FONT_FAMILIES = {
  DISPLAY: 'StrawberrySansDisplay',
  TEXT: 'StrawberrySansText'
};

// Font weights
export const FONT_WEIGHTS = {
  LIGHT: 300,
  REGULAR: 400,
  MEDIUM: 500,
  SEMIBOLD: 600,
  BOLD: 700
};

// Spacing scale
export const SPACING = {
  XS: 4,
  SM: 8,
  MD: 16,
  LG: 24,
  XL: 32,
  XXL: 48
};

// Border radius scale
export const BORDER_RADIUS = {
  SM: 4,
  MD: 8,
  LG: 12,
  XL: 16,
  ROUND: '50%'
};

// Preset colors for theme customization
export const PRIMARY_PRESET_COLORS = [
  { name: 'Red', value: '#FF4D4F' },
  { name: 'Blue', value: '#1890FF' },
  { name: 'Green', value: '#52C41A' },
  { name: 'Purple', value: '#722ED1' },
  { name: 'Orange', value: '#FA8C16' },
  { name: 'Pink', value: '#eb2f96' },
  { name: 'Cyan', value: '#13c2c2' },
  { name: 'Gold', value: '#faad14' },
];

export const SECONDARY_PRESET_COLORS = [
  { name: 'Light Purple', value: '#CE93D8' },
  { name: 'Teal', value: '#20B2AA' },
  { name: 'Indigo', value: '#3F51B5' },
  { name: 'Amber', value: '#FFC107' },
  { name: 'Deep Purple', value: '#673AB7' },
  { name: 'Lime', value: '#CDDC39' },
  { name: 'Deep Orange', value: '#FF5722' },
  { name: 'Light Blue', value: '#03A9F4' },
];

// Page layout and scrolling styles
export const PAGE_LAYOUT = {
  // Main page container that ensures proper scrolling
  pageContainer: {
    minHeight: '100vh',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent horizontal scroll
    position: 'relative',
  },
  
  // Content area that allows vertical scrolling
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
    minHeight: 0, // Important for flex children
    position: 'relative',
    // Smooth scrolling behavior
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
  },
  
  // Full height layout for app structure
  fullHeightLayout: {
    height: '100vh',
    maxHeight: '100vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  
  // Main content wrapper
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  
  // Page content with proper spacing - matches header width constraints
  pageContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme => theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.2)' 
        : 'rgba(255, 77, 79, 0.2)',
      borderRadius: '20px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      transition: 'background 0.2s ease',
      '&:hover': {
        background: theme => theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.3)' 
          : 'rgba(255, 77, 79, 0.3)',
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: theme => theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.2) transparent' 
      : 'rgba(255, 77, 79, 0.2) transparent',
  },
  
  // Content wrapper that matches header width constraints
  contentWrapper: {
    maxWidth: '1536px', // xl breakpoint
    width: '100%',
    margin: '0 auto',
    px: { xs: 0, sm: 2 },
    position: 'relative',
  },
  
  // Page content with header-matching constraints
  constrainedPageContent: {
    maxWidth: '1536px', // xl breakpoint - matches header Container maxWidth="xl"
    width: '100%',
    margin: '0 auto',
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 3 },
    position: 'relative',
  },
  
  // Responsive page content
  responsivePageContent: {
    flex: 1,
    padding: {
      xs: '16px',
      sm: '20px',
      md: '24px',
      lg: '32px'
    },
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    maxWidth: '1536px', // xl breakpoint
    width: '100%',
    margin: '0 auto',
  },
  
  // Fixed header that doesn't scroll
  fixedHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1100,
    backgroundColor: 'background.paper',
    borderBottom: '1px solid',
    borderColor: 'divider',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  
  // Fixed footer that doesn't scroll
  fixedFooter: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1100,
    backgroundColor: 'background.paper',
    borderTop: '1px solid',
    borderColor: 'divider',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  
  // Sidebar layout
  sidebarLayout: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
  
  // Sidebar content
  sidebar: {
    width: '280px',
    flexShrink: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: 'background.paper',
    borderRight: '1px solid',
    borderColor: 'divider',
  },
  
  // Main area next to sidebar
  mainArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  },
  
  // Scrollable content area for main content sections
  scrollableContentArea: {
    height: 'calc(100vh - 149px)', // Updated calculation: 85 (header) + 48 (page padding) + 16 (grid spacing)
    maxHeight: 'calc(100vh - 149px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: 1, // Space for scrollbar
    paddingBottom: 3, // Reduced bottom padding for bottom-touching
  },
  
  // Sidebar + scrollable main content layout
  sidebarScrollableLayout: {
    container: {
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      gap: 3,
    },
    sidebar: {
      width: '280px',
      flexShrink: 0,
      height: 'fit-content',
      position: 'sticky',
      top: 24,
    },
    mainContent: {
      flex: 1,
      height: 'calc(100vh - 149px)', // Updated calculation: 85 (header) + 48 (page padding) + 16 (grid spacing)
      maxHeight: 'calc(100vh - 149px)',
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingRight: 1,
      paddingBottom: 3, // Reduced bottom padding for bottom-touching
    },
  },
  
  // Dialog layout configurations
  splitDialog: {
    container: {
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      height: { md: '490px' },
      minHeight: '400px'
    },
    sidebar: {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
      padding: 3,
      width: { xs: '100%', md: '240px' },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    },
    mainContent: {
      flex: 1,
      padding: 3,
      display: 'flex',
      flexDirection: 'column'
    }
  },
  
  standardDialog: {
    container: {
      padding: 3,
      minWidth: 400,
      maxWidth: 600
    }
  },
  
  fullWidthDialog: {
    container: {
      padding: 3,
      width: '100%',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh'
    }
  },
  
  wizardDialog: {
    container: {
      padding: 3,
      minWidth: 600,
      maxWidth: 800,
      minHeight: 500
    }
  },
  
  // Feature card layout
  featureCard: {
    flex: 1,
    padding: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    backgroundColor: 'background.paper',
    transition: 'all 0.2s ease'
  },
  
  // Upload zone layout
  uploadZone: {
    marginTop: 3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    borderRadius: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    border: '2px dashed',
    borderColor: 'divider',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  },
  
  // Dialog info section
  dialogInfoSection: {
    opacity: 0.8,
    marginTop: 4
  },
  
  // Dialog tip section
  dialogTip: {
    marginTop: 2,
    textAlign: 'center'
  }
};

// Common styles for consistent UI patterns
export const COMMON_STYLES = {
  card: {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    bgcolor: 'background.paper',
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: theme => theme.palette.mode === 'dark' 
        ? '0 4px 12px rgba(0, 0, 0, 0.1)' 
        : '0 4px 12px rgba(5, 3, 21, 0.06)',
    }
  },
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme => theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.2)' 
        : 'rgba(255, 77, 79, 0.2)',
      borderRadius: '20px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      transition: 'background 0.2s ease',
      '&:hover': {
        background: theme => theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.3)' 
          : 'rgba(255, 77, 79, 0.3)',
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: theme => theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.2) transparent' 
      : 'rgba(255, 77, 79, 0.2) transparent',
  },
  
  // Enhanced scrollbar for main content areas
  mainScrollbar: {
    '&::-webkit-scrollbar': {
      width: '12px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme => theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.15)' 
        : 'rgba(255, 77, 79, 0.15)',
      borderRadius: '6px',
      border: '3px solid transparent',
      backgroundClip: 'padding-box',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: theme => theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.25)' 
          : 'rgba(255, 77, 79, 0.25)',
        borderRadius: '6px',
      },
      '&:active': {
        background: theme => theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.35)' 
          : 'rgba(255, 77, 79, 0.35)',
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
      borderRadius: '6px',
    },
    '&::-webkit-scrollbar-corner': {
      background: 'transparent',
    },
    scrollbarWidth: 'auto',
    scrollbarColor: theme => theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.15) transparent' 
      : 'rgba(255, 77, 79, 0.15) transparent',
  },
  
  // Thin scrollbar for smaller areas
  thinScrollbar: {
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme => theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.2)' 
        : 'rgba(255, 77, 79, 0.2)',
      borderRadius: '3px',
      '&:hover': {
        background: theme => theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.3)' 
          : 'rgba(255, 77, 79, 0.3)',
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    scrollbarWidth: 'thin',
  },
  
  iconButton: {
    color: 'text.secondary',
    '&:hover': {
      color: 'primary.main',
      bgcolor: theme => theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(255, 77, 79, 0.08)'
    }
  },
  paperOverlay: {
    bgcolor: 'background.paper',
    borderBottom: '1px solid',
    borderColor: 'divider',
    boxShadow: theme => theme.palette.mode === 'dark' 
      ? '0 2px 8px rgba(0, 0, 0, 0.05)' 
      : '0 2px 8px rgba(5, 3, 21, 0.04)',
  },
  sectionTitle: {
    fontFamily: 'StrawberrySansDisplay',
    fontWeight: 500,
    color: 'text.secondary',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontSize: '0.875rem',
  },
  glassmorphism: {
    backdropFilter: 'blur(16px) saturate(180%)',
    WebkitBackdropFilter: 'blur(16px) saturate(180%)',
    backgroundColor: theme => theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.7)',
    border: '1px solid',
    borderColor: theme => theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.3)',
    boxShadow: theme => `
      0 8px 32px ${theme.palette.mode === 'dark' 
        ? 'rgba(0, 0, 0, 0.3)' 
        : 'rgba(0, 0, 0, 0.08)'},
      inset 0 0 0 1px ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(255, 255, 255, 0.2)'}
    `,
  },
  constructionPattern: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: theme => `repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 10px,
        ${theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.06)' 
          : 'rgba(255, 77, 79, 0.06)'} 10px,
        ${theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.06)' 
          : 'rgba(255, 77, 79, 0.06)'} 20px
      )`,
      backgroundSize: '28px 28px',
      animation: 'construction 8s linear infinite',
      pointerEvents: 'none',
      zIndex: 0,
      opacity: 0.5
    }
  },
  dotPattern: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -50,
      left: -50,
      right: -50,
      bottom: -50,
      backgroundImage: theme => `radial-gradient(${theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.03)' 
        : 'rgba(255, 77, 79, 0.03)'} 1px, transparent 1px)`,
      backgroundSize: '20px 20px',
      animation: 'moveBackground 20s linear infinite',
      opacity: 0.5,
      pointerEvents: 'none'
    }
  },
  gradientBackground: {
    background: theme => `
      radial-gradient(circle at 90% 90%, ${theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.06)' 
        : 'rgba(255, 77, 79, 0.06)'}, transparent 50%),
      radial-gradient(circle at 10% 10%, ${theme.palette.mode === 'dark' 
        ? 'rgba(206, 147, 216, 0.06)' 
        : 'rgba(206, 147, 216, 0.06)'}, transparent 50%),
      ${theme.palette.background.paper}
    `,
  },
  shimmerEffect: {
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme => `linear-gradient(
        90deg,
        ${theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0)' 
          : 'rgba(255, 255, 255, 0)'} 0%,
        ${theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.6)' 
          : 'rgba(255, 255, 255, 0.6)'} 50%,
        ${theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0)' 
          : 'rgba(255, 255, 255, 0)'} 100%
      )`,
      transform: 'translateX(-100%)',
      animation: 'shimmer 2s infinite',
    }
  },
  pulseGlow: {
    animation: 'pulseBoxShadow 2s infinite',
  },
  floatingAnimation: {
    animation: 'float 20s ease-in-out infinite',
    '@keyframes float': {
      '0%, 100%': {
        transform: 'translate(0, 0)',
      },
      '50%': {
        transform: 'translate(5%, 5%)',
      },
    },
  },
  spinAnimation: {
    animation: 'spin 1.5s linear infinite',
  },
  fadeInAnimation: {
    animation: 'fadeIn 0.5s ease-out',
  }
};

// Animation keyframes
export const ANIMATIONS = {
  fadeIn: {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  spin: {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  pulseBoxShadow: {
    '0%': {
      boxShadow: '0 0 0 0 rgba(255, 77, 79, 0.7)'
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(255, 77, 79, 0)'
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(255, 77, 79, 0)'
    }
  },
  construction: {
    '0%': {
      backgroundPosition: '0 0',
    },
    '100%': {
      backgroundPosition: '56px 56px',
    },
  },
  moveBackground: {
    '0%': {
      transform: 'translate(0, 0)'
    },
    '100%': {
      transform: 'translate(20px, 20px)'
    }
  },
  shimmer: {
    '100%': {
      transform: 'translateX(100%)',
    },
  },
  float: {
    '0%, 100%': {
      transform: 'translate(0, 0)',
    },
    '50%': {
      transform: 'translate(5%, 5%)',
    },
  }
};

// Z-index scale
export const Z_INDEX = {
  DROPDOWN: 1000,
  STICKY: 1020,
  FIXED: 1030,
  MODAL_BACKDROP: 1040,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070,
  TOAST: 1080
};

// Breakpoints
export const BREAKPOINTS = {
  XS: 0,
  SM: 600,
  MD: 900,
  LG: 1200,
  XL: 1536
};

// Shadow scale
export const SHADOWS = {
  SM: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  MD: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  LG: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  XL: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  INNER: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
};

// Transition durations
export const TRANSITIONS = {
  FAST: '150ms',
  NORMAL: '200ms',
  SLOW: '300ms',
  SLOWER: '500ms'
};

// Common transition easings
export const EASINGS = {
  EASE_IN: 'cubic-bezier(0.4, 0, 1, 1)',
  EASE_OUT: 'cubic-bezier(0, 0, 0.2, 1)',
  EASE_IN_OUT: 'cubic-bezier(0.4, 0, 0.2, 1)',
  BOUNCE: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
}; 